/**
 * Extracts progress percentage from response objects
 * @param {Object} body - Response object containing progress info
 * @returns {number|null} Progress percentage (0-100) or null if not found
 */
export function extractPercentage(body) {
  if (!body || typeof body !== 'object') return null;

  const candidates = [
    body.percentage,
    body.progress,
    body.percent,
    body.data?.percentage,
    body.data?.progress,
    body.result?.percentage,
  ];

  for (const candidate of candidates) {
    if (candidate != null && !isNaN(Number(candidate))) {
      return Math.min(100, Math.max(0, Number(candidate)));
    }
  }

  return null;
}

/**
 * Extracts error message from error response objects
 * @param {Object} body - Error response object
 * @param {string} [defaultPrefix=''] - Optional prefix for the error message
 * @returns {string} Extracted error message or default message
 */
export function extractErrorMessage(body, defaultPrefix = '') {
  if (!body || typeof body !== 'object') {
    return defaultPrefix || 'An unknown error occurred';
  }

  const candidates = [
    body.error,
    body.errorMessage,
    body.message,
    body.detail,
    body.data?.error,
    body.data?.message,
    body.result?.error,
  ];

  for (const candidate of candidates) {
    if (candidate) return String(candidate);
  }

  return defaultPrefix || 'An unknown error occurred';
}

/**
 * Extracts revised prompt from preview or status data
 * @param {Object} data - Response data object
 * @returns {string|null} Revised prompt or null if not found
 */
export function extractRevisedPrompt(data) {
  if (!data || typeof data !== 'object') return null;

  return data.revisedPrompt || data.promptUsed || null;
}

/**
 * Extracts file name from response data
 * @param {Object} data - Response data object
 * @param {string} [defaultFilename='animation.mp4'] - Default filename
 * @returns {string} Extracted filename or default
 */
export function extractFilename(data, defaultFilename = 'animation.mp4') {
  if (!data || typeof data !== 'object') return defaultFilename;

  return data.filename || defaultFilename;
}

/**
 * Extracts asset name from response data
 * @param {Object} data - Response data object
 * @param {string} [defaultName='Animation'] - Default asset name
 * @returns {string} Extracted asset name or default
 */
export function extractAssetName(data, defaultName = 'Animation') {
  if (!data || typeof data !== 'object') return defaultName;

  return data.assetName || data.name || defaultName;
}

/**
 * Extracts thumbnail from response data
 * @param {Object} data - Response data object
 * @returns {string|null} Base64 thumbnail or null if not found
 */
export function extractThumbnail(data) {
  if (!data || typeof data !== 'object') return null;

  return data.thumbnail_base64 || data.thumbnail || null;
}